import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { playDrop } from "../utils/waterDropSound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PressureWashReveal — scroll-driven dirty concrete → clean concrete.
 * As user scrolls through, a "wand" travels diagonally across a stained
 * sidewalk, revealing the restored surface behind. Steam/spray particles
 * emit at the wand tip.
 *
 * Same physics-story motif as SqueegeeReveal but tuned for pressure washing.
 */
export default function PressureWashReveal({
  headline = "1,800 PSI.",
  italic = "Twenty years of grime, one dawn shift.",
  copy = "Hot water at 200°F. Municipal-grade wands. Reclaim-and-vac trailers so nothing runs into the storm drain. We restore sidewalks, storefronts, and drive-aprons back to the concrete tone your architect specified — without touching the mortar joints.",
  stat = { value: "200°F", label: "Hot-water reclaim" },
}) {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [cleanImage, setCleanImage] = useState("");
  const soundFiredRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/media/services`).then((r) => {
      const raw = r.data?.media || {};
      const base = process.env.REACT_APP_BACKEND_URL;
      const url = raw["transform-1-after"];
      if (url) setCleanImage(url.startsWith("http") ? url : `${base}${url}`);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    function onScroll() {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height + vh;
      const seen = vh - rect.top;
      setProgress(Math.max(0, Math.min(1, seen / total)));
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // 0..0.15 build-up, 0.15..0.85 wand travel, 0.85..1 settle
  const wash = Math.max(0, Math.min(1, (progress - 0.15) / 0.7));

  // Fire a single water-drop sound cue as the wand finishes
  useEffect(() => {
    if (wash >= 0.92 && !soundFiredRef.current) {
      soundFiredRef.current = true;
      playDrop({ pitch: 0.7, volume: 0.55 });
    } else if (wash < 0.4) {
      soundFiredRef.current = false;
    }
  }, [wash]);
  // Diagonal wipe — reveal grows from top-left corner
  const revealX = wash * 100;   // 0 → 100 %
  const revealY = wash * 100;
  // Clip path: diagonal reveal (polygon)
  const clip = `polygon(0 0, ${revealX + 20}% 0, ${revealX - 5}% 100%, 0 100%)`;

  // Wand position (tip)
  const wandLeft = `${revealX}%`;
  const wandTop = `${revealY * 0.6 + 10}%`; // roughly diagonal

  return (
    <section
      ref={sectionRef}
      className="relative"
      style={{ background: "var(--bg)", padding: "120px 0" }}
      data-testid="pressure-wash-reveal"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-16"
        >
          <div className="md:col-span-3 overline pt-2">The pressure test</div>
          <div className="md:col-span-9">
            <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              {headline}{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>{italic}</span>
            </h2>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Story left */}
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--gold)" }}>
              Method · Hot-Water Surface Clean
            </div>
            <p className="mt-6 font-body" style={{ color: "var(--text-2)", fontSize: 17, lineHeight: 1.7 }}>
              {copy}
            </p>
            <div className="mt-10 pt-6" style={{ borderTop: "1px solid var(--line)" }}>
              <div className="font-display leading-none" style={{ fontSize: "clamp(2.5rem, 3.4vw, 3.4rem)", color: "var(--gold-2)", letterSpacing: "-0.03em" }}>
                {stat.value}
              </div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.38em]" style={{ color: "var(--text-3)" }}>
                {stat.label}
              </div>
            </div>
          </div>

          {/* Interactive frame right */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            <div
              className="relative w-full overflow-hidden"
              style={{
                aspectRatio: "16 / 10",
                borderRadius: "28px",
                border: "1px solid var(--line-gold)",
                boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03) inset",
              }}
            >
              {/* Clean concrete (base) — from admin-uploaded transform-1-after */}
              {cleanImage ? (
                <img
                  src={cleanImage}
                  alt="Restored concrete"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: "brightness(0.78) contrast(1.08) saturate(0.9)" }}
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0" style={{
                  background: "linear-gradient(180deg, #1e1e22 0%, #14141a 100%)",
                }} />
              )}

              {/* Dirty concrete overlay (SVG generated tone + stains) — clipped diagonally */}
              <div
                aria-hidden
                className="absolute inset-0"
                style={{ clipPath: `polygon(${revealX}% 0, 100% 0, 100% 100%, ${revealX - 10}% 100%)`, transition: "clip-path 0.05s linear" }}
              >
                {/* Base grime */}
                <div className="absolute inset-0" style={{
                  background: "linear-gradient(160deg, rgba(48,44,38,0.72) 0%, rgba(72,64,52,0.68) 60%, rgba(58,50,42,0.75) 100%)",
                }} />
                {/* Grimy fractal noise */}
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='3' seed='3'/><feColorMatrix values='0 0 0 0 0.35  0 0 0 0 0.3  0 0 0 0 0.22  0 0 0 0.75 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
                    opacity: 0.55,
                    mixBlendMode: "multiply",
                  }}
                />
                {/* Stains */}
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                  {[
                    [15, 30, 8], [40, 22, 6], [65, 40, 9], [82, 26, 5],
                    [22, 55, 7], [50, 65, 10], [72, 72, 6], [90, 60, 5],
                    [30, 82, 8], [58, 88, 6], [82, 92, 7],
                  ].map(([cx, cy, r], i) => (
                    <ellipse key={i} cx={cx} cy={cy} rx={r} ry={r * 0.6}
                      fill="rgba(30,26,20,0.45)"
                    />
                  ))}
                </svg>
              </div>

              {/* Wet reflective sheen after wand */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none transition-opacity duration-700"
                style={{
                  opacity: wash > 0.3 ? 0.5 : 0,
                  background: `linear-gradient(120deg, transparent ${Math.max(0, revealX - 20)}%, rgba(255,255,255,0.1) ${revealX}%, transparent ${revealX + 15}%)`,
                  mixBlendMode: "screen",
                }}
              />

              {/* Wand — angled black-and-gold industrial */}
              <div
                aria-hidden
                className="absolute origin-bottom-left"
                style={{
                  left: wandLeft, top: wandTop,
                  transform: "translate(-6px, -75%) rotate(-32deg)",
                  width: "140px", height: "6px",
                  background: "linear-gradient(90deg, #2b2c30, #6E7076 45%, #C6A87C 65%, #2b2c30 100%)",
                  borderRadius: "3px",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.6), 0 0 22px rgba(232,217,197,0.18)",
                  opacity: wash > 0.02 && wash < 0.99 ? 1 : 0,
                  transition: "opacity 0.4s ease",
                }}
              />
              {/* Spray cone from wand tip */}
              <div
                aria-hidden
                className="absolute pointer-events-none"
                style={{
                  left: wandLeft, top: wandTop,
                  width: "80px", height: "80px",
                  transform: "translate(-95%, -50%)",
                  background: "radial-gradient(circle at 100% 50%, rgba(255,255,255,0.28), rgba(232,217,197,0.12) 30%, transparent 60%)",
                  filter: "blur(4px)",
                  opacity: wash > 0.02 && wash < 0.99 ? 1 : 0,
                  transition: "opacity 0.4s ease",
                  mixBlendMode: "screen",
                }}
              />
              {/* Fine spray droplets emitting from tip */}
              {wash > 0.02 && wash < 0.99 && (
                <>
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      aria-hidden
                      className="absolute rounded-full"
                      style={{
                        left: `calc(${wandLeft} - ${20 + i * 6}px)`,
                        top: `calc(${wandTop} - ${5 + Math.sin(i) * 4}px)`,
                        width: `${3 + (i % 3)}px`,
                        height: `${3 + (i % 3)}px`,
                        background: "rgba(255,255,255,0.55)",
                        boxShadow: "0 0 6px rgba(255,255,255,0.5)",
                        opacity: 0.85 - i * 0.1,
                      }}
                    />
                  ))}
                </>
              )}

              {/* Progress meter */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text)" }}>
                <span style={{ opacity: 0.75 }}>Stained</span>
                <div className="flex-1 mx-4 h-px relative" style={{ background: "rgba(255,255,255,0.15)" }}>
                  <div
                    className="absolute top-0 left-0 h-full transition-all duration-100"
                    style={{ width: `${wash * 100}%`, background: "var(--gold)", boxShadow: "0 0 8px var(--gold)" }}
                  />
                </div>
                <span style={{ opacity: 0.75, color: "var(--gold-2)" }}>Restored</span>
              </div>
            </div>

            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.36em] text-center" style={{ color: "var(--text-3)" }}>
              ↕ Scroll to run the wand
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
